import React, { FC } from "react";
import PanelHeading from "../PanelHeading";
import StepFooter from "../StepFooter";
import NoticeBar from "../NoticeBar";
import { PanelProps } from "./types";
import { useLocale } from "../../i18n/locale";
import { useReservation } from "../reservationContext";
import { formatStayDate } from "../../i18n/formatDate";

/**
 * Step 1.
 *
 * Where the tenant lands. Before anything is asked of them they are shown the
 * stay this paperwork belongs to — the reference treated the welcome as
 * already done and opened on identity, which left someone following a link
 * from an email with no confirmation they were onboarding the right booking.
 *
 * The stay summary carries the same details in the right column, but it is
 * hidden below 1100px and is deliberately unchanging context rather than the
 * subject of the screen. Repeating them here is what makes the step a step.
 */
const WelcomePanel: FC<PanelProps> = ({ steps, onBack, onContinue }) => {
  const { locale, t } = useLocale();
  const c = t.desktop.welcomePanel;
  // The same labels the stay summary uses, so the two never drift apart and
  // nothing new has to be translated.
  const stay = t.desktop.stay;
  const reservation = useReservation();
  const upcoming = steps.filter(
    (step) => step.id !== "welcome" && step.phase === "before"
  );
  const hasAfterArrival = steps.some((step) => step.phase === "after");

  return (
    <>
      <PanelHeading eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />

      <section className="mt-6 border border-line bg-cream p-[26px]">
        <small className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-gold">
          {reservation.district}
        </small>
        <h3 className="mt-[7px] font-serif text-[21px] font-normal text-navy">
          {reservation.property}
        </h3>
        <p className="mt-1.5 text-[12px] leading-[1.6] text-[#7c8388]">
          {reservation.address}
        </p>

        <dl className="mt-[22px] grid grid-cols-2 gap-[14px] border-t border-line pt-[18px]">
          <div>
            <dt className="text-[10px] uppercase tracking-[0.1em] text-[#9b9fa2]">
              {stay.checkIn}
            </dt>
            <dd className="text-[13.5px] font-bold text-navy">
              {formatStayDate(reservation.checkIn, locale)}
            </dd>
          </div>
          <div className="border-l border-line pl-[18px]">
            <dt className="text-[10px] uppercase tracking-[0.1em] text-[#9b9fa2]">
              {stay.checkOut}
            </dt>
            <dd className="text-[13.5px] font-bold text-navy">
              {formatStayDate(reservation.checkOut, locale)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-[0.1em] text-[#9b9fa2]">
              {stay.tenantName}
            </dt>
            <dd className="text-[13.5px] font-bold text-navy">
              {reservation.tenantName}
            </dd>
          </div>
          <div className="border-l border-line pl-[18px]">
            <dt className="text-[10px] uppercase tracking-[0.1em] text-[#9b9fa2]">
              {stay.bookingReference}
            </dt>
            <dd className="text-[13.5px] font-bold text-navy">
              {reservation.bookingReference}
            </dd>
          </div>
        </dl>
      </section>

      <h3 className="mt-8 text-[13px] font-bold uppercase tracking-[0.12em] text-navy">
        {c.heading}
      </h3>

      <ol className="mt-4 border-y border-line">
        {upcoming.map((step, i) => {
          const copy = t.desktop.steps[step.id];

          return (
            <li
              key={step.id}
              className={`flex items-center gap-5 py-5 ${
                i > 0 ? "border-t border-line" : ""
              }`}
            >
              <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-cream font-serif text-[15px] text-navy">
                {i + 1}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold text-navy">
                  {copy.rail}
                </span>
                <span className="mt-0.5 block text-[12px] text-[#95999d]">
                  {copy.caption}
                </span>
              </span>

              <span className="flex-none text-[11px] uppercase tracking-[0.1em] text-[#a1a6aa]">
                {c.minutes(step.minutes)}
              </span>
            </li>
          );
        })}
      </ol>

      {hasAfterArrival && (
        <NoticeBar
          className="mt-8"
          title={t.desktop.afterArrival}
          body={c.afterArrivalNote}
        />
      )}

      <StepFooter onBack={onBack} onContinue={onContinue} label={c.cta} />
    </>
  );
};

export default WelcomePanel;
